import React, { useEffect, useMemo, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "react-query";
import { useCart } from "../../context/CartContext";
import ProductDeliveryPromo from "./ProductDeliveryPromo";
import api from "../../services/api";
import "../../styles/quickview.css";
import {
  FiX,
  FiShoppingCart,
  FiHeart,
  FiChevronLeft,
  FiChevronRight,
  FiStar,
  FiMinus,
  FiPlus,
} from "react-icons/fi";
import toast from "react-hot-toast";

const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300' viewBox='0 0 300 300'%3E%3Crect width='300' height='300' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='45%25' font-family='Arial' font-size='48' fill='%23ddd' text-anchor='middle' dy='.3em'%3E📦%3C/text%3E%3Ctext x='50%25' y='60%25' font-family='Arial' font-size='14' fill='%23999' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";

const getVariationAttributes = (attributes) => {
  const source =
    typeof attributes === "string"
      ? (() => {
          try {
            return JSON.parse(attributes);
          } catch {
            return {};
          }
        })()
      : attributes;

  return Object.entries(source && typeof source === "object" ? source : {})
    .map(([name, value]) => [
      String(name).trim(),
      Array.isArray(value)
        ? value.filter(Boolean).join(", ")
        : value !== null && typeof value === "object"
          ? ""
          : String(value ?? "").trim(),
    ])
    .filter(([name, value]) => name && value);
};

const QuickViewModal = ({ product, onClose, onAddToCart }) => {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [selectedVariationId, setSelectedVariationId] = useState(null);

  // Controls the exit animation. We don't unmount immediately on close —
  // we flip this flag, let AnimatePresence play the exit transition, and
  // only tell the parent to actually remove us once that finishes.
  const [isClosing, setIsClosing] = useState(false);

  const requestClose = useCallback(() => {
    setIsClosing(true);
  }, []);

  const selectedVariation =
    product?.variations?.find(
      (variation) => variation.VariationID === selectedVariationId,
    ) || null;
  const displayProduct = selectedVariation
    ? { ...product, ...selectedVariation }
    : product;

  // Scroll-preserving body lock
  useEffect(() => {
    const scrollY = window.scrollY;
    const body = document.body;
    const html = document.documentElement;

    const originalBodyOverflow = body.style.overflow;
    const originalBodyPosition = body.style.position;
    const originalBodyTop = body.style.top;
    const originalBodyWidth = body.style.width;
    const originalScrollBehavior = html.style.scrollBehavior;

    html.style.scrollBehavior = "auto";

    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";

    return () => {
      body.style.overflow = originalBodyOverflow;
      body.style.position = originalBodyPosition;
      body.style.top = originalBodyTop;
      body.style.width = originalBodyWidth;

      window.scrollTo(0, scrollY);

      requestAnimationFrame(() => {
        html.style.scrollBehavior = originalScrollBehavior;
      });
    };
  }, []);

  // Reset gallery + quantity when product changes
  useEffect(() => {
    setActiveImage(0);
    setQuantity(1);
    setSelectedVariationId(null);
  }, [product?.ProductID]);

  // Close on Escape, same smooth path as every other close trigger
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [requestClose]);

  // Fetch product reviews
  const { data: reviewsData } = useQuery(
    ["quickview-reviews", product?.ProductID],
    async () => {
      if (!product?.ProductID) return null;
      const response = await api.get(
        `/api/reviews/product/${product.ProductID}`,
      );
      return response.data;
    },
    {
      enabled: !!product?.ProductID,
      staleTime: 60000,
      retry: 1,
    },
  );

  const reviews = reviewsData?.reviews || [];
  const averageRating =
    reviewsData?.averageRating ?? product?.reviewStats?.rating ?? 0;
  const totalReviews =
    reviewsData?.totalReviews ?? product?.reviewStats?.count ?? 0;

  // Extract images
  const images = useMemo(() => {
    if (!product) return [PLACEHOLDER_IMAGE];
    const collectedImages = [];

    const addImage = (value) => {
      if (!value) return;
      if (Array.isArray(value)) {
        value.forEach(addImage);
        return;
      }
      if (typeof value === "object" && value !== null) {
        if (value.url || value.src)
          collectedImages.push(value.url || value.src);
        return;
      }
      if (typeof value !== "string") return;

      const trimmed = value.trim();
      if (!trimmed) return;

      if (trimmed.startsWith("[")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            parsed.forEach(addImage);
            return;
          }
        } catch (e) {
          /* ignore */
        }
      }

      if (trimmed.includes("|")) {
        trimmed.split("|").forEach(addImage);
        return;
      }

      if (trimmed.includes(",") && trimmed.includes("http")) {
        const parts = trimmed.split(",").map((p) => p.trim());
        if (parts.length > 1) {
          parts.forEach(addImage);
          return;
        }
      }

      collectedImages.push(trimmed);
    };

    if (selectedVariation?.VariationImages) {
      addImage(selectedVariation.VariationImages);
    }
    if (
      collectedImages.length === 0 &&
      product.images &&
      Array.isArray(product.images)
    ) {
      product.images.forEach(addImage);
    }
    if (collectedImages.length === 0) {
      addImage(displayProduct.ImageUrl);
      addImage(product.Images);
      addImage(product.ImageUrls);
      addImage(product.AdditionalImages);
      addImage(product.ImageData);
    }

    const filtered = collectedImages.filter(
      (img, idx, self) => img && self.indexOf(img) === idx,
    );
    return filtered.length > 0 ? filtered : [PLACEHOLDER_IMAGE];
  }, [product, selectedVariation, displayProduct]);

  // Price with promo
  const sellingPrice = parseFloat(displayProduct?.SellingPrice || 0);
  const originalPrice = parseFloat(product?.OriginalPrice || 0);
  const hasDiscount = originalPrice > sellingPrice && sellingPrice > 0;
  const discountPercent = hasDiscount
    ? Math.round(((originalPrice - sellingPrice) / originalPrice) * 100)
    : 0;

  // Features
  const features = useMemo(() => {
    if (Array.isArray(product?.features) && product.features.length > 0) {
      return product.features.slice(0, 4);
    }
    const derived = [];
    if (product?.Feature1) derived.push(product.Feature1);
    if (product?.Feature2) derived.push(product.Feature2);
    if (product?.Feature3) derived.push(product.Feature3);
    if (product?.Feature4) derived.push(product.Feature4);
    return derived;
  }, [product]);

  const isInStock = (displayProduct?.StockQuantity ?? 1) > 0;

  // Handlers
  const handleAddToCart = async (e) => {
    e.stopPropagation();
    try {
      if (onAddToCart) {
        await onAddToCart(product, quantity);
      } else {
        await addToCart(product.ProductID || product.id, quantity, {
          name: displayProduct.Name,
          price: sellingPrice,
          originalPrice,
          imageUrl: images[0],
          sku: displayProduct.SKU,
          variationId: selectedVariation?.VariationID,
        });
      }
      toast.success(`${product.Name} added to cart!`);
      requestClose();
    } catch (err) {
      console.error(err);
      toast.error("Failed to add to cart");
    }
  };

  const handleViewFullDetails = (e) => {
    e.stopPropagation();
    requestClose();
    navigate(`/product/${product.ProductID}`);
  };

  const handleQuantityChange = (type) => {
    if (type === "increase" && quantity < (product.StockQuantity || 99)) {
      setQuantity((q) => q + 1);
    } else if (type === "decrease" && quantity > 1) {
      setQuantity((q) => q - 1);
    }
  };

  const goToImage = (idx) => setActiveImage(idx);
  const nextImage = () => setActiveImage((i) => (i + 1) % images.length);
  const prevImage = () =>
    setActiveImage((i) => (i - 1 + images.length) % images.length);

  const renderStars = (ratingValue = averageRating, size = 16) => {
    const rounded = Math.round(ratingValue);
    return (
      <div className="qv-stars">
        {[1, 2, 3, 4, 5].map((s) => (
          <FiStar
            key={s}
            size={size}
            className={`qv-star ${s <= rounded ? "filled" : ""}`}
          />
        ))}
      </div>
    );
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.charAt(0).toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = [
      "#2c7da0",
      "#61a5c2",
      "#89c2d9",
      "#2a9d8f",
      "#e9c46a",
      "#f4a261",
      "#e76f51",
    ];
    return colors[(name?.length || 0) % colors.length];
  };

  if (!product) return null;

  const modalContent = (
    // AnimatePresence stays mounted for the whole lifetime of the modal.
    // We only ever remove its child (via `isClosing`), which is what lets
    // the exit animation actually play. `onExitComplete` is where we tell
    // the parent it's safe to unmount QuickViewModal entirely.
    <AnimatePresence onExitComplete={onClose}>
      {!isClosing && (
        <motion.div
          key="qv-overlay"
          className="qv-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          onClick={requestClose}
        >
          <motion.div
            className="qv-modal"
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{
              type: "spring",
              damping: 32,
              stiffness: 350,
              mass: 0.8,
            }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            onDragEnd={(_, info) => {
              // Past the threshold: hand off straight into the exit
              // animation from wherever the sheet currently sits, instead
              // of snapping back to origin first. Anything short of that
              // threshold naturally springs back via dragConstraints.
              if (info.offset.y > 80 || info.velocity.y > 400) {
                requestClose();
              }
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Visual indicator bar for mobile swipe down */}
            <div className="qv-drag-handle" />

            <button
              className="qv-close"
              onClick={requestClose}
              aria-label="Close"
            >
              <FiX />
            </button>

            {/* Left: Gallery (Main image on top, thumbs below) */}
            <div className="qv-gallery">
              <div className="qv-main-image">
                {product.isBestSeller && (
                  <div className="qv-badge qv-badge-best">
                    <FiStar size={12} /> Best Seller
                  </div>
                )}

                <img
                  src={images[activeImage]}
                  alt={product.Name}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = PLACEHOLDER_IMAGE;
                  }}
                />

                {images.length > 1 && (
                  <>
                    <button
                      className="qv-nav qv-nav-prev"
                      onClick={prevImage}
                      aria-label="Previous image"
                    >
                      <FiChevronLeft />
                    </button>
                    <button
                      className="qv-nav qv-nav-next"
                      onClick={nextImage}
                      aria-label="Next image"
                    >
                      <FiChevronRight />
                    </button>

                    <div className="qv-image-counter">
                      {activeImage + 1} / {images.length}
                    </div>
                  </>
                )}
              </div>

              {images.length > 1 && (
                <div className="qv-thumbs">
                  {images.slice(0, 6).map((img, idx) => (
                    <button
                      key={idx}
                      className={`qv-thumb ${idx === activeImage ? "active" : ""}`}
                      onClick={() => goToImage(idx)}
                      aria-label={`View image ${idx + 1}`}
                    >
                      <img
                        src={img}
                        alt=""
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = PLACEHOLDER_IMAGE;
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Info */}
            <div className="qv-info">
              {product.CategoryName && (
                <div className="qv-eyebrow">
                  {product.CategoryName.toUpperCase()}
                </div>
              )}

              <h2 className="qv-title">{displayProduct.Name}</h2>
              {(product.SupplierName || product.SupplierLocation) && (
                <div className="qv-supplier-info">
                  {product.SupplierName && <span>Supplier: {product.SupplierName}</span>}
                  {product.SupplierLocation && <span>Location: {product.SupplierLocation}</span>}
                </div>
              )}

              {/* Rating summary (Stars + rating value) */}
              {averageRating > 0 && (
                <div className="qv-rating-row">
                  {renderStars(averageRating, 16)}
                  <span className="qv-rating-value">
                    {Number(averageRating).toFixed(1)}
                  </span>
                  <span className="qv-rating-count">
                    ({totalReviews} {totalReviews === 1 ? "review" : "reviews"})
                  </span>
                </div>
              )}

              {Array.isArray(product.variations) &&
                product.variations.length > 0 && (
                  <div className="qv-variations">
                    <div className="qv-section-label">Available variations</div>
                    <div className="qv-variation-list">
                      {product.variations.map((variation) => (
                        <div
                          className={`qv-variation-chip ${
                            selectedVariationId === variation.VariationID
                              ? "selected"
                              : ""
                          }`}
                          key={variation.VariationID}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            setSelectedVariationId(variation.VariationID);
                            setActiveImage(0);
                            setQuantity(1);
                          }}
                        >
                          <img
                            src={
                              variation.ImageUrl ||
                              variation.VariationImages?.[0]?.ImageUrl ||
                              PLACEHOLDER_IMAGE
                            }
                            alt=""
                          />
                          <strong>{variation.Name}</strong>
                          {getVariationAttributes(variation.Attributes).length >
                            0 && (
                            <small>
                              {getVariationAttributes(variation.Attributes)
                                .map(([, value]) => value)
                                .join(" / ")}
                            </small>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Price row */}
              <div className="qv-price-row">
                <span className="qv-price-current">
                  R{sellingPrice.toLocaleString()}
                </span>
                {hasDiscount && (
                  <>
                    <span className="qv-price-original">
                      R{originalPrice.toLocaleString()}
                    </span>
                    <span className="qv-discount-badge">
                      {discountPercent}% OFF
                    </span>
                  </>
                )}
              </div>
              <ProductDeliveryPromo />

              {/* Features grid */}
              {features.length > 0 && (
                <div className="qv-features">
                  {features.map((feature, idx) => (
                    <div key={idx} className="qv-feature">
                      <div className="qv-feature-icon">
                        {feature.icon || "✓"}
                      </div>
                      <div className="qv-feature-label">
                        {feature.label || feature}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Quantity + Add to Cart */}
              <div className="qv-actions-row">
                <div className="qv-qty">
                  <button
                    className="qv-qty-btn"
                    onClick={() => handleQuantityChange("decrease")}
                    disabled={quantity <= 1}
                    aria-label="Decrease"
                  >
                    <FiMinus size={14} />
                  </button>
                  <span className="qv-qty-value">{quantity}</span>
                  <button
                    className="qv-qty-btn"
                    onClick={() => handleQuantityChange("increase")}
                    disabled={quantity >= (product.StockQuantity || 99)}
                    aria-label="Increase"
                  >
                    <FiPlus size={14} />
                  </button>
                </div>
                <button
                  className="qv-add-to-cart"
                  onClick={handleAddToCart}
                  disabled={!isInStock}
                >
                  <FiShoppingCart size={16} />
                  {isInStock ? "Add to Cart" : "Out of Stock"}
                </button>
              </div>

              {/* Wishlist button */}
              <button
                className={`qv-wishlist-btn ${isWishlisted ? "active" : ""}`}
                onClick={() => setIsWishlisted((v) => !v)}
              >
                <FiHeart size={16} />
                {isWishlisted ? "Added to Wishlist" : "Add to Wishlist"}
              </button>

              {/* Stock + delivery */}
              <div className="qv-footer">
                <span className={`qv-stock ${isInStock ? "in" : "out"}`}>
                  <span className="qv-stock-dot" />
                  {isInStock ? "In Stock" : "Out of Stock"}
                </span>
              </div>

              {/* Customer Reviews Section (Hidden on mobile via CSS) */}
              {reviews.length > 0 && (
                <div className="qv-reviews-section">
                  <div className="qv-reviews-header">
                    <h3 className="qv-reviews-title">Customer Reviews</h3>
                    <span className="qv-reviews-count">
                      {totalReviews} {totalReviews === 1 ? "review" : "reviews"}
                    </span>
                  </div>

                  <div className="qv-reviews-grid">
                    {reviews.slice(0, 4).map((review) => (
                      <div key={review.ReviewID} className="qv-review-tile">
                        <div className="qv-review-tile-header">
                          <div
                            className="qv-review-avatar"
                            style={{
                              background: getAvatarColor(review.ReviewerName),
                            }}
                          >
                            {getInitials(review.ReviewerName)}
                          </div>
                          <div className="qv-review-tile-info">
                            <span className="qv-review-name">
                              {review.ReviewerName || "Verified Customer"}
                            </span>
                            <span className="qv-review-date">
                              {review.CreatedAt
                                ? new Date(review.CreatedAt).toLocaleDateString(
                                    "en-US",
                                    {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    },
                                  )
                                : ""}
                            </span>
                          </div>
                        </div>
                        <div className="qv-review-tile-stars">
                          {renderStars(review.Rating || 0, 13)}
                        </div>
                        <p className="qv-review-tile-comment">
                          {review.Comment?.substring(0, 140)}
                          {review.Comment?.length > 140 ? "..." : ""}
                        </p>
                      </div>
                    ))}
                  </div>

                  {reviews.length > 4 && (
                    <button
                      className="qv-reviews-see-all"
                      onClick={handleViewFullDetails}
                    >
                      See all {totalReviews} reviews →
                    </button>
                  )}
                </div>
              )}

              {/* View full details link */}
              <button
                className="qv-view-details"
                onClick={handleViewFullDetails}
              >
                View full details →
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default QuickViewModal;
